import React, { useState, useEffect, useRef } from "react";
import "./swaggerPage.css";
import Breadcrumb from "../breadcrumb/breadcrumb";
import Container from "../container/container";
import { connect } from "react-redux";
import { getRouteUrl } from "../../routers";
import { ROUTE_PRODUCT_VIEW_SINGLE } from "../../routers/content/product";
import { buildTableOfContents } from "../../utils/swaggerParser";
import SwaggerContentRenderer from "./SwaggerContentRenderer";
import APIProductService from "../../services/APIProductService";
import RedocTryoutManager from "../redoc-tryout/redoc-tryoutManager";
import { convertObj } from "swagger2openapi";
import * as actions from "../../actions";

function SwaggerPage(props) {
  const {
    breadcrumb,
    alias,
    uuid,
    title,
    apiTitle,
    swaggerData,
    apiEndpoint,
    productData,
    loading,
    error,
  } = props;

  // Local storage keys
  const STORAGE_KEYS = {
    LEFT_PANEL_WIDTH: "swagger_leftPanelWidth",
  };

  // Helper function to get value from localStorage with fallback
  const getStoredValue = (key, defaultValue) => {
    try {
      const stored = localStorage.getItem(key);
      if (stored !== null) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error(`Error reading from localStorage:`, error);
    }
    return defaultValue;
  };

  // State for panel widths - initialized from localStorage
  const [leftPanelWidth, setLeftPanelWidth] = useState(() =>
    getStoredValue(STORAGE_KEYS.LEFT_PANEL_WIDTH, 25)
  );
  const [isResizing, setIsResizing] = useState(false);
  const [resizingPanel, setResizingPanel] = useState(null);

  // State for table of contents and selected item
  const [tableOfContents, setTableOfContents] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filteredDescription, setFilteredDescription] = useState(null);

  // State for collapsed sections - all section-groups initially collapsed
  const [collapsedSections, setCollapsedSections] = useState({
    endpoints: true, // API Endpoints section initially collapsed
    entities: true, // Data Models section initially collapsed
    security: true, // Authentication section initially collapsed
  });

  // State for OpenAPI converted swagger for tryout
  const [openApiSpec, setOpenApiSpec] = useState(null);
  const [isConverting, setIsConverting] = useState(false);

  // State for tour data
  const [tourData, setTourData] = useState(null);

  // State for global view mode
  const [globalViewMode, setGlobalViewMode] = useState("table");

  // State for uploaded swagger (when user uploads a file)
  const [uploadedSwaggerData, setUploadedSwaggerData] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const fileInputRef = useRef(null);

  // Expose a global trigger to open the upload dialog (used by top menu)
  useEffect(() => {
    const triggerUpload = () => {
      try {
        if (fileInputRef.current) {
          fileInputRef.current.click();
        }
      } catch (e) {
        console.error("Unable to open upload dialog:", e);
      }
    };
    window.triggerSwaggerUpload = triggerUpload;
    return () => {
      delete window.triggerSwaggerUpload;
    };
  }, []);

  const handleUploadFileChange = (e) => {
    try {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const text = reader.result;
          let spec;
          try {
            spec = JSON.parse(text);
          } catch (jsonErr) {
            console.error("Uploaded Swagger must be valid JSON.", jsonErr);
            alert("Failed to parse JSON Swagger file.");
            return;
          }
          setUploadedSwaggerData(spec);
          setUploadedFileName(file.name || "uploaded-swagger.json");
          setSelectedItem(null);
        } catch (err) {
          console.error("Error handling uploaded Swagger:", err);
        } finally {
          // Reset input value so user can re-upload same file if needed
          e.target.value = "";
        }
      };
      reader.readAsText(file);
    } catch (err) {
      console.error("Upload failed:", err);
    }
  };

  // Remove the body class addition since we'll use the standard header

  // Function to generate tour data from OpenAPI spec
  const generateTourData = (spec) => {
    // Check if we already have tour data from Redux
    if (props.tourData && props.tourData.length > 0) {
      setTourData(props.tourData);
      return;
    }

    if (!spec || !spec.paths) {
      return;
    }

    // Generate sample tour data from available endpoints
    const sampleTourData = [];
    const paths = Object.keys(spec.paths);

    // Select up to 5 representative endpoints for the tour
    const operationTypes = ["get", "post", "put", "delete", "patch"];

    for (const operationType of operationTypes) {
      for (const path of paths) {
        if (spec.paths[path][operationType] && sampleTourData.length < 5) {
          const operation = spec.paths[path][operationType];
          sampleTourData.push({
            path: path,
            method: operationType,
            description:
              operation.summary ||
              `Demo ${operationType.toUpperCase()} operation for ${path}`,
            parameters: operation.parameters || [],
            security: operation.security || [],
            documentationId: `swagger-${uuid}-${apiTitle}`,
          });
          break; // Move to next operation type
        }
      }
    }

    // If we don't have enough diverse operations, just take the first few
    if (sampleTourData.length < 3) {
      for (const path of paths.slice(0, 5)) {
        const methods = Object.keys(spec.paths[path]);
        for (const method of methods) {
          if (sampleTourData.length >= 5) break;

          // Skip if we already have this endpoint
          if (
            sampleTourData.some(
              (item) => item.path === path && item.method === method
            )
          ) {
            continue;
          }

          const operation = spec.paths[path][method];
          sampleTourData.push({
            path: path,
            method: method,
            description:
              operation.summary ||
              `Demo ${method.toUpperCase()} operation for ${path}`,
            parameters: operation.parameters || [],
            security: operation.security || [],
            documentationId: `swagger-${uuid}-${apiTitle}`,
          });
        }
      }
    }

    if (sampleTourData.length > 0) {
      console.log(
        "Generated tour data with",
        sampleTourData.length,
        "endpoints:",
        sampleTourData
      );
      setTourData(sampleTourData);
    } else {
      console.log("No tour data generated - no endpoints found in spec");
    }
  };

  // Build table of contents and get filtered description when swagger data changes
  useEffect(() => {
    const effectiveSwaggerData = uploadedSwaggerData || swaggerData;
    if (effectiveSwaggerData) {
      // Get the filtered description using APIProductService
      let description = effectiveSwaggerData.info?.description;

      if (apiEndpoint && !uploadedSwaggerData) {
        // Set the API document in the service
        APIProductService.apiDocument = apiEndpoint;

        // Get the filtered description
        const filtered = APIProductService.getSwaggerDescription();
        if (filtered !== null) {
          description = filtered;
        }
        setFilteredDescription(filtered);
      }

      // Create a modified swagger data with the filtered description
      const modifiedSwaggerData = {
        ...effectiveSwaggerData,
        info: {
          ...effectiveSwaggerData.info,
          description: description,
        },
      };

      const toc = buildTableOfContents(modifiedSwaggerData);
      setTableOfContents(toc);

      // Initialize collapsed state for all section-groups except the first expandable one
      const newCollapsedState = {};
      let firstExpandableSection = null;

      toc.forEach((item) => {
        if (
          item.type === "section-group" &&
          item.children &&
          item.children.length > 0
        ) {
          // Collapse all section-groups initially
          newCollapsedState[item.id] = true;
          if (!firstExpandableSection && item.id === "endpoints") {
            firstExpandableSection = item.id;
          }
        }
      });

      // Expand the API Endpoints section by default if it exists
      if (firstExpandableSection) {
        newCollapsedState[firstExpandableSection] = false;
      }

      setCollapsedSections(newCollapsedState);

      // Select first non-section-group item by default
      if (toc.length > 0) {
        // Try to find the first non-section-group item
        let itemToSelect = toc[0];
        for (const item of toc) {
          if (item.type !== "section-group") {
            itemToSelect = item;
            break;
          }
        }
        setSelectedItem(itemToSelect);
      }

      // Convert swagger to OpenAPI 3.0 for tryout functionality
      setIsConverting(true);
      const options = { patch: true, warnOnly: true, text: "{}" };
      convertObj(effectiveSwaggerData, options, (err, res) => {
        if (err) {
          console.error("Error converting swagger to OpenAPI:", err);
          // Even if conversion fails, try to use the original spec
          setOpenApiSpec(effectiveSwaggerData);
          setIsConverting(false);
          return;
        }

        // Use the converted OpenAPI spec
        if (res && res.openapi) {
          setOpenApiSpec(res.openapi);
          setIsConverting(false);
          // Generate tour data from the converted spec
          generateTourData(res.openapi);
        } else {
          // Fallback to original spec if conversion doesn't produce expected result
          setOpenApiSpec(effectiveSwaggerData);
          setIsConverting(false);
          // Generate tour data from the original spec
          generateTourData(effectiveSwaggerData);
        }
      });
    }
  }, [swaggerData, apiEndpoint, uploadedSwaggerData]);

  // Handle TOC item selection
  const handleTocItemClick = (item) => {
    console.log("[SWAGGER SELECTION] TOC item clicked:", {
      id: item.id,
      type: item.type,
      title: item.title,
      path: item.path,
      method: item.method,
      timestamp: new Date().toISOString(),
      previousSelection: selectedItem
        ? {
            id: selectedItem.id,
            type: selectedItem.type,
            path: selectedItem.path,
            method: selectedItem.method,
          }
        : null,
    });
    // For endpoints, the full data is already included from the parser
    setSelectedItem(item);
  };

  // Handle section collapse toggle
  const handleSectionToggle = (sectionId) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Get raw content for the selected item
  const getRawContent = (item) => {
    if (!item) return "No item selected";

    // Handle different item types
    switch (item.type) {
      case "info":
        // For info section, return the entire info object as JSON
        return JSON.stringify(item.content, null, 2);

      case "documentation":
        // For documentation, return the raw markdown/HTML content
        return item.content || "No content available";

      case "endpoint":
        // For endpoints, return the full endpoint specification
        return JSON.stringify(
          {
            method: item.method,
            path: item.path,
            summary: item.summary,
            description: item.description,
            operationId: item.operationId,
            parameters: item.parameters,
            requestBody: item.requestBody,
            responses: item.responses,
            tags: item.tags,
            security: item.security,
          },
          null,
          2
        );

      case "entity":
        // For entities/data models, return the schema
        return JSON.stringify(
          {
            name: item.title,
            schema: item.schema,
            required: item.required,
            properties: item.properties,
          },
          null,
          2
        );

      case "security":
        // For security schemes, return the scheme definition
        return JSON.stringify(
          {
            name: item.title,
            scheme: item.scheme,
          },
          null,
          2
        );

      case "section-group":
      case "endpoint-group":
        // For groups, show a summary
        return JSON.stringify(
          {
            title: item.title,
            type: item.type,
            childrenCount: item.children ? item.children.length : 0,
            children: item.children
              ? item.children.map((c) => ({
                  id: c.id,
                  title: c.title || c.path,
                  type: c.type,
                }))
              : [],
          },
          null,
          2
        );

      default:
        // For any other type, try to return the entire item as JSON
        return JSON.stringify(item, null, 2);
    }
  };

  // Render TOC item
  const renderTocItem = (item, level = 0) => {
    // Skip rendering section-groups that have no children
    if (
      item.type === "section-group" &&
      (!item.children || item.children.length === 0)
    ) {
      return null;
    }

    const isSelected = selectedItem?.id === item.id;
    const isCollapsible =
      item.type === "section-group" &&
      item.children &&
      item.children.length > 0;
    const isCollapsed = collapsedSections[item.id] || false;

    return (
      <div key={item.id} className="toc-item-wrapper">
        <div
          className={`toc-item toc-level-${level} ${
            isSelected && !isCollapsible ? "selected" : ""
          } toc-type-${item.type} ${isCollapsible ? "collapsible" : ""}`}
          onClick={() => {
            if (isCollapsible) {
              handleSectionToggle(item.id);
            } else {
              handleTocItemClick(item);
            }
          }}
          style={{ paddingLeft: `${level * 15 + 10}px` }}
        >
          {isCollapsible && (
            <span className="collapse-icon">{isCollapsed ? "▸" : "▾"}</span>
          )}
          {item.type === "endpoint" && (
            <span
              className={`method-badge-small method-${item.method.toLowerCase()}`}
            >
              {item.method}
            </span>
          )}
          <span className="toc-title">{item.title || item.path}</span>
        </div>
        {item.children &&
          !isCollapsed &&
          item.children.map((child) => renderTocItem(child, level + 1))}
      </div>
    );
  };

  // Save to localStorage helper
  const saveToStorage = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Error saving to localStorage:`, error);
    }
  };

  // No collapsible panels – only resizable splitter

  // Handle mouse down on resizer
  const handleMouseDown = (panel) => (e) => {
    setIsResizing(true);
    setResizingPanel(panel);
    e.preventDefault();
  };

  // Handle mouse move for resizing
  React.useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || !resizingPanel) return;

      const container = document.querySelector(".swagger-container");
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const mouseX = e.clientX - containerRect.left;
      const percentage = (mouseX / containerWidth) * 100;

      if (resizingPanel === "left") {
        // Resizing left panel
        const newWidth = Math.min(40, Math.max(10, percentage));
        setLeftPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      if (isResizing && resizingPanel === "left") {
        // Save the final width to localStorage
        saveToStorage(STORAGE_KEYS.LEFT_PANEL_WIDTH, leftPanelWidth);
      }
      setIsResizing(false);
      setResizingPanel(null);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, resizingPanel, leftPanelWidth]);

  // Calculate center width based on panel states
  const centerWidth = 100 - leftPanelWidth;
  const documentationId = uploadedSwaggerData
    ? `swagger-upload-${uploadedFileName || "file"}`
    : `swagger-${uuid}-${apiTitle}`;
  const effectiveSwaggerDataForRender = uploadedSwaggerData || swaggerData;

  // Expose global Try Out trigger and validity flag
  useEffect(() => {
    const isValid = !!selectedItem && selectedItem.type === "endpoint";
    window.swaggerTryOutValid = isValid;
    try {
      window.dispatchEvent(
        new CustomEvent("swagger-tryout-validity", { detail: { valid: isValid } })
      );
    } catch (e) {
      // no-op
    }
    window.triggerSwaggerTryOut = () => {
      try {
        if (!isValid) {
          return false;
        }
        const path = selectedItem.path;
        const method = (selectedItem.method || "").toLowerCase();
        const parameters = selectedItem.parameters || [];
        const security = selectedItem.security || [];
        const form = {
          query: {},
          header: {},
          path: {},
          body: selectedItem.requestBody ? JSON.stringify({}, null, 2) : null,
        };
        // Ensure tryout exists, then show it
        props.createTryout({
          documentationId,
          path,
          method,
          parameters,
          security,
          form,
        });
        props.showTryout({
          documentationId,
          path,
          method,
          parameters,
          security,
        });
        return true;
      } catch (e) {
        console.error("Failed to trigger Try Out:", e);
        return false;
      }
    };
    return () => {
      delete window.swaggerTryOutValid;
      delete window.triggerSwaggerTryOut;
    };
  }, [selectedItem, documentationId]);

  return (
    <div>
      <Breadcrumb
        {...breadcrumb}
        params={{
          product: {
            name: title,
            href: alias
              ? alias
              : getRouteUrl(ROUTE_PRODUCT_VIEW_SINGLE, { apiProductId: uuid }),
          },
        }}
      />

      {/* Hidden upload input for Swagger JSON files */}
      <input
        type="file"
        accept="application/json,.json"
        style={{ display: "none" }}
        ref={fileInputRef}
        onChange={handleUploadFileChange}
      />

      <div className={`swagger-container ${isResizing ? "resizing" : ""}`}>
        {/* Left Panel (TOC) */}
        <div
          className="swagger-panel swagger-panel-left"
          style={{ width: `${leftPanelWidth}%` }}
        >
          <div className="panel-inner">
            <div className="panel-content">
              <div className="swagger-toc">
                {tableOfContents.map((item) => renderTocItem(item))}
              </div>
            </div>
          </div>
          <div
            className="panel-resizer panel-resizer-right"
            onMouseDown={handleMouseDown("left")}
          />
        </div>

        {/* Center Content */}
        <div className="swagger-center" style={{ width: `${centerWidth}%` }}>
          <Container backgroundGrid>
            <div className="center-content">
              {loading && (
                <div className="loading-message">
                  Loading API documentation...
                </div>
              )}
              {error && <div className="error-message">Error: {error}</div>}
              {!loading && !error && effectiveSwaggerDataForRender && selectedItem && (
                <>
                  {console.log(
                    "[TRYOUT DEBUG] SwaggerPage rendering SwaggerContentRenderer with:",
                    {
                      documentationId,
                      hasCreateTryout: !!props.createTryout,
                      hasShowTryout: !!props.showTryout,
                      hasUpdateTryout: !!props.updateTryout,
                      selectedItemType: selectedItem.type,
                    }
                  )}
                  <SwaggerContentRenderer
                    content={selectedItem.content}
                    type={selectedItem.type}
                    data={selectedItem}
                    swaggerData={effectiveSwaggerDataForRender}
                    createTryout={props.createTryout}
                    showTryout={props.showTryout}
                    updateTryout={props.updateTryout}
                    documentationId={documentationId}
                    globalViewMode={globalViewMode}
                  />
                </>
              )}
              {!loading && !error && !effectiveSwaggerDataForRender && (
                <div className="no-data-message">
                  No swagger documentation available
                </div>
              )}
            </div>
          </Container>
        </div>

        {/* Right panel removed */}
      </div>

      {/* Render RedocTryoutManager for try out functionality */}
      {openApiSpec && !isConverting && (
        <>
          {console.log(
            "[TRYOUT DEBUG] SwaggerPage rendering RedocTryoutManager with:",
            {
              documentationId,
              hasOpenApiSpec: !!openApiSpec,
              productUuid: uuid,
              apiTitle,
            }
          )}
          <RedocTryoutManager
            swaggerJson={openApiSpec}
            documentationId={documentationId}
            productUuid={uuid}
            tourData={tourData || props.tourData}
            selectedItem={selectedItem}
            createTryout={props.createTryout}
            showTryout={props.showTryout}
            updateTryout={props.updateTryout}
            tryoutRequestCompleted={props.tryoutRequestCompleted}
            tryouts={props.tryouts}
            isLoggedIn={props.isLoggedIn}
            applicationUser={props.applicationUser}
            applications={props.applications}
            clearAuthCode={props.clearAuthCode}
            setAuthCode={props.setAuthCode}
            getAccessToken={props.getAccessToken}
            setAccessToken={props.setAccessToken}
            tryoutResponseClear={props.tryoutResponseClear}
            clearTryoutHistory={props.clearTryoutHistory}
            favoriteTryoutHistoryItem={props.favoriteTryoutHistoryItem}
            deleteTryoutHistoryItem={props.deleteTryoutHistoryItem}
            importTryoutHistory={props.importTryoutHistory}
            editTryoutDescription={props.editTryoutDescription}
            fromSwaggerPage={true}
          />
        </>
      )}
    </div>
  );
}

const mapStateToProps = (state, ownProps) => {
  // Generate documentationId same way as in the component
  const documentationId = `swagger-${ownProps.uuid}-${ownProps.apiTitle}`;

  // Try to get tour data from Redux store
  const tourData = state.apiDocumentation?.byId?.[documentationId]?.tour;

  return {
    isLoggedIn: state.applicationUser?.isLoggedIn,
    applicationUser: state.applicationUser,
    applications: state.applicationUser?.applications,
    tryouts: state.tryout,
    tourData: tourData,
  };
};

const mapDispatchToProps = {
  createTryout: actions.createTryout,
  showTryout: actions.showTryout,
  updateTryout: actions.updateTryout,
  tryoutRequestCompleted: actions.tryoutRequestCompleted,
  clearAuthCode: actions.clearAuthCode,
  setAuthCode: actions.setAuthCode,
  getAccessToken: actions.getAccessToken,
  setAccessToken: actions.setAccessToken,
  tryoutResponseClear: actions.tryoutResponseClear,
  clearTryoutHistory: actions.clearTryoutHistory,
  favoriteTryoutHistoryItem: actions.favoriteTryoutHistoryItem,
  deleteTryoutHistoryItem: actions.deleteTryoutHistoryItem,
  importTryoutHistory: actions.importTryoutHistory,
  editTryoutDescription: actions.editTryoutDescription,
};

export default connect(mapStateToProps, mapDispatchToProps)(SwaggerPage);
